import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { invalidateAfter } from "../api/query-registry";

export interface StatusHistoryEntry {
  id: string;
  status: string;
  startedAt: string; // YYYY-MM-DD
  endedAt: string | null; // YYYY-MM-DD, or null while ongoing
}

export interface StatusHistoryBody {
  status: string;
  startedAt: string;
  endedAt?: string | null;
}

/** CRUD for manual status-period records (quick-view timeline). The resident's
 *  current status is separate — these entries are the historical log and never
 *  change residents.status (that is owned by the PATCH transition). */
export function useStatusHistory(residentId: string) {
  const qc = useQueryClient();
  const invalidate = () => invalidateAfter(qc, "residentChanged");
  const add = useMutation({
    mutationFn: (body: StatusHistoryBody) =>
      api<StatusHistoryEntry>(`/api/residents/${residentId}/status-history`, { method: "POST", body }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ entryId, body }: { entryId: string; body: Partial<StatusHistoryBody> }) =>
      api<StatusHistoryEntry>(`/api/residents/${residentId}/status-history/${entryId}`, { method: "PATCH", body }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (entryId: string) =>
      api<{ ok: boolean }>(`/api/residents/${residentId}/status-history/${entryId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
  return { add, update, remove };
}
